import type { SeoLine } from './seoLine'

export interface PageMetaInput {
  slug: string
  name: string
  lines: SeoLine[]
  hasStepFree?: boolean
  /** Travelcard zone as printed by TfL — `'2'`, but also `'1+2'`, `'2/3'`. */
  zone?: string
}

export interface PageMeta {
  title: string
  description: string
  canonical: string
  og: {
    title: string
    description: string
    url: string
    image: string
    type: 'place'
  }
  twitter: {
    card: 'summary_large_image'
    image: string
  }
}

const BASE_URL = 'https://london-tube.com'
const SHARED_OG_IMAGE = `${BASE_URL}/og-image.png`

/**
 * Locale segment of a station page URL. Station copy is English-only, so
 * there is exactly one canonical station URL per station. Pinned against
 * the build-time scheme by `src/seo/programmaticPageUrls.test.ts`.
 */
const CANONICAL_LOCALE = 'en'

/** Lines beyond this many are folded into a `+N` suffix in the title. */
const TITLE_LINE_LIMIT = 2

/**
 * `['District']` → `District`
 * `['District', 'Piccadilly']` → `District & Piccadilly`
 * `['Circle', 'Hammersmith & City', 'Metropolitan']` → `Circle, Hammersmith & City`
 *
 * Two names read best joined by `&` — but only when neither carries an
 * ampersand of its own, or `Circle & Hammersmith & City` reads as three
 * lines. Commas everywhere else.
 */
function joinLineNames(names: string[]): string {
  const ambiguous = names.some((n) => n.includes('&'))
  if (names.length === 2 && !ambiguous) return `${names[0]} & ${names[1]}`
  return names.join(', ')
}

/**
 * SEO head copy for a station page.
 *
 * TASK-588: the previous template was
 * `<name> Station — London Underground | london-tube.com`, which spent
 * its first 30 characters on words every competing result also has and
 * pushed the differentiators — zone, lines — off the end. Google
 * Search Console showed ~800 impressions/day at position ~10 with a
 * 0.3% CTR against them.
 *
 * The replacement front-loads what the query actually asks:
 * `Barons Court Tube — Zone 2 · District & Piccadilly · map, times, exits`.
 * `Tube` rather than `Underground` because that is the UK search term
 * (see metro-aso keyword baselines), and the trailing triple names the
 * three things the page answers.
 *
 * The brand suffix is gone on purpose: Google appends the site name
 * itself, so spending characters on it truncated the tail instead.
 */
/** Translator shape shared with `scripts/stationPageMeta.mjs`. */
export type MetaTranslator = (key: string, params?: Record<string, string | number>) => string

/**
 * English templates, verbatim from the pre-localization literals so the
 * no-translator output is byte-identical. The build-time mirror carries
 * the same table; `stationPageMetaParity.test.ts` compares both.
 */
const EN_STRINGS: Readonly<Record<string, string>> = Object.freeze({
  station_title: '{{station}} Tube Station: {{tail}}',
  station_title_zone: 'Zone {{zone}}',
  station_title_lines_one: '{{lines}} line',
  station_title_lines_other: '{{lines}} lines',
  station_title_suffix: 'map & routes',
  station_lines_phrase_one: 'the {{lines}} line',
  station_lines_phrase_other: 'the {{lines}} lines',
  station_description:
    '{{station}} tube station{{zonePart}}{{linesPart}}. See it on the live London Underground map, check the next stations in each direction and plan tube routes from {{station}}.',
  station_description_zone_part: ' in Zone {{zone}}',
  station_description_lines_part: ' on {{linesPhrase}}',
  station_og_title: '{{station}} Tube Station',
})

/** `t` over {@link EN_STRINGS} — the no-translator default. */
export function englishMetaT(key: string, params: Record<string, string | number> = {}): string {
  const template = EN_STRINGS[key]
  if (typeof template !== 'string') throw new Error(`meta: unknown key "${key}"`)
  return template.replaceAll(/\{\{(\w+)\}\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m,
  )
}

export function buildPageMeta(input: PageMetaInput, t: MetaTranslator = englishMetaT): PageMeta {
  const lineNames = input.lines.map((l) => l.name)
  const url = `${BASE_URL}/${CANONICAL_LOCALE}/stations/${input.slug}`

  const shown = lineNames.slice(0, TITLE_LINE_LIMIT)
  const hidden = lineNames.length - shown.length
  const titleLines = hidden > 0 ? `${joinLineNames(shown)} +${hidden}` : joinLineNames(shown)
  // 2026-10-08: the tail answers the station query (zone, lines) and
  // names what the page adds (map, routes); "on the Tube map — … · live
  // map" sold the map to people asking about the station — 0.32% CTR at
  // positions 7–10 against 0.97% for route pages on the same positions.
  const titleTail = [
    input.zone ? t('station_title_zone', { zone: input.zone }) : null,
    titleLines
      ? t(lineNames.length === 1 ? 'station_title_lines_one' : 'station_title_lines_other', {
          lines: titleLines,
        })
      : null,
    t('station_title_suffix'),
  ]
    .filter(Boolean)
    .join(', ')
  const title = t('station_title', { station: input.name, tail: titleTail })

  const linesPhrase =
    lineNames.length > 0
      ? t(lineNames.length === 1 ? 'station_lines_phrase_one' : 'station_lines_phrase_other', {
          lines: joinLineNames(lineNames),
        })
      : ''
  const description = t('station_description', {
    station: input.name,
    zonePart: input.zone ? t('station_description_zone_part', { zone: input.zone }) : '',
    linesPart: lineNames.length > 0 ? t('station_description_lines_part', { linesPhrase }) : '',
  })

  return {
    title,
    description,
    canonical: url,
    og: {
      title: t('station_og_title', { station: input.name }),
      description,
      url,
      image: SHARED_OG_IMAGE,
      type: 'place',
    },
    twitter: {
      card: 'summary_large_image',
      image: SHARED_OG_IMAGE,
    },
  }
}
